import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * MiniCalendar — Command-page calendar.
 * Layout: Left sidebar lists "Due Today" (all kinds), right side a big month grid
 * where each day cell shows its items directly (title + kind tag) — not just counts.
 */
export default function MiniCalendar() {
  const [anchor, setAnchor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [overdue, setOverdue] = useState([]);  // iter52 — folded into today's cell
  const [expandDay, setExpandDay] = useState(null);  // iter50 — Outlook-style "X more" expand

  const [monthStart, monthEnd] = useMemo(() => {
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
    return [s, e];
  }, [anchor]);

  useEffect(() => {
    api.get("/calendar", {
      params: { start: monthStart.toISOString().slice(0, 10), end: monthEnd.toISOString().slice(0, 10) },
    }).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [monthStart, monthEnd]);

  // iter52 — overdue items surface in the "Today" cell with X-days-late markers
  useEffect(() => {
    api.get("/me/home").then((r) => setOverdue(r.data?.overdue || [])).catch(() => setOverdue([]));
  }, []);

  const cells = useMemo(() => {
    const firstDow = monthStart.getDay();
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(monthStart);
      d.setDate(d.getDate() - firstDow + i);
      cells.push(d);
    }
    return cells;
  }, [monthStart]);

  const byDate = useMemo(() => {
    const map = {};
    for (const it of items) {
      const key = (it.due_date || "").slice(0, 10);
      if (!key) continue;
      (map[key] ||= []).push(it);
    }
    return map;
  }, [items]);

  const today = new Date().toISOString().slice(0, 10);

  // iter52 — inject overdue items into today's cell with days_overdue computed
  const todayCellItems = useMemo(() => {
    const dueToday = (byDate[today] || []).filter((it) => it.status !== "completed");
    const overdueDecorated = overdue.map((it) => ({
      ...it,
      days_overdue: it.due_date ? Math.max(0, Math.floor((Date.now() - new Date(it.due_date).getTime()) / 86400000)) : 0,
    }));
    // iter54 — today's items on top, overdue BELOW (least-late first so oldest is at the bottom)
    overdueDecorated.sort((a, b) => (a.days_overdue || 0) - (b.days_overdue || 0));
    return [...dueToday, ...overdueDecorated];
  }, [byDate, today, overdue]);

  const todayItems = todayCellItems;
  const monthLabel = anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <section className="surface-card rounded-2xl overflow-hidden" data-testid="dashboard-mini-calendar">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[#1a1a1a]/08 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Month view</div>
          <div className="font-display font-bold text-base">Calendar — {monthLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
            data-testid="mini-cal-prev"
            className="p-1.5 rounded-lg hover:bg-[#f7f5f2] text-[#1a1a1a]/70">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setAnchor(new Date())} data-testid="mini-cal-today"
            className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] px-2">
            Today
          </button>
          <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
            data-testid="mini-cal-next"
            className="p-1.5 rounded-lg hover:bg-[#f7f5f2] text-[#1a1a1a]/70">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Layout: left sidebar (Due Today) + right (month grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-4">
        {/* Due Today sidebar */}
        <div className="lg:col-span-1 bg-[#1a1a1a] text-white p-4" data-testid="mini-cal-due-today">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15] font-bold">// Today · {today.slice(5)}</div>
          <div className="font-display font-black text-2xl mt-1 mb-3">Due Today</div>
          {todayItems.length === 0 ? (
            <div className="text-[12px] text-white/55 font-mono uppercase tracking-wider">Nothing due today.</div>
          ) : (
            <ul className="space-y-1.5">
              {todayItems.map((it) => (
                <li key={`${it.kind || it.type}-${it.id}`}>
                  <ItemInList it={it} dark />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Month grid */}
        <div className="lg:col-span-3 p-3">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={i} className="text-center text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 font-bold py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0 border-t border-l border-[#1a1a1a]/12">
            {cells.map((d, i) => {
              const key = d.toISOString().slice(0, 10);
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = key === today;
              const dayItems = isToday ? todayCellItems : (byDate[key] || []);
              const openItems = dayItems.filter((it) => it.status !== "completed");
              const hasOverdue = openItems.length > 0 && key < today;
              return (
                <div key={i} data-testid={`mini-cal-day-${key}`}
                  className={`min-h-[76px] border-r border-b border-[#1a1a1a]/12 p-1 flex flex-col ${
                    !inMonth ? "bg-[#f7f5f2]/50" :
                    isToday ? "bg-[#fff1ea]" : "bg-white"
                  }`}>
                  <div className={`text-[10px] font-bold mb-1 ${
                    isToday ? "text-[#ff4500]" :
                    hasOverdue ? "text-[#ff4500]" :
                    !inMonth ? "text-[#1a1a1a]/30" : "text-[#1a1a1a]/70"
                  }`}>{d.getDate()}</div>
                  <div className="space-y-0.5 overflow-hidden flex-1">
                    {dayItems.slice(0, 3).map((it) => (
                      <ItemInCell key={`${it.kind || it.type}-${it.id}`} it={it} />
                    ))}
                    {dayItems.length > 3 && (
                      <button
                        data-testid={`mini-cal-more-${key}`}
                        onClick={() => setExpandDay({ date: key, items: dayItems })}
                        className="text-[8px] font-mono uppercase tracking-wider font-bold text-[#ff4500] hover:underline text-left w-full"
                      >
                        +{dayItems.length - 3} more…
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* iter50 — Day expander (Outlook style) */}
      <Dialog open={!!expandDay} onOpenChange={(o) => !o && setExpandDay(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-lg">
              {expandDay?.date && new Date(expandDay.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1">
            {expandDay?.items.length} item{expandDay?.items.length === 1 ? "" : "s"} due
          </div>
          <ul className="divide-y divide-[#1a1a1a]/08 max-h-[60vh] overflow-y-auto">
            {(expandDay?.items || []).map((it) => (
              <li key={`${it.kind || it.type}-${it.id}`} className="py-1">
                <ItemInList it={it} />
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ItemInCell({ it }) {
  const k = it.kind || it.type;
  const linkTo = k === "task" ? "/app/tasks" :
    k === "vv" ? `/app/vv/${it.id}` :
    k === "meeting" ? `/app/meetings/${it.id}` :
    k === "subtask" && it.parent_action_id ? `/app/actions/${it.parent_action_id}` :
    `/app/actions/${it.id}`;
  const isDone = ["completed", "closed", "resolved"].includes((it.status || "").toLowerCase());
  const color = k === "vv" ? "text-[#a13ad6]"
    : k === "meeting" ? "text-[#1a5fb4]"
    : k === "task" ? "text-[#1a8754]"
    : "text-[#ff4500]";
  const prefix = k === "vv" ? "V&V" : k === "meeting" ? "Meet" : k === "task" ? "Task" : "Action";
  const daysLate = it.days_overdue || 0;  // iter52
  return (
    <Link to={linkTo} data-testid={`mini-cal-item-${it.id}`}
      className={`block truncate text-[10px] leading-tight hover:underline ${isDone ? "italic line-through opacity-55" : "font-bold"}`}>
      <span className={`font-mono font-bold text-[8px] uppercase tracking-wider mr-1 ${color}`}>{prefix}</span>
      <span className="text-[#1a1a1a]">{it.title}</span>
      {daysLate > 0 && (
        <span className="ml-1 inline-block bg-[#ff4500] text-white font-mono text-[7px] font-bold uppercase tracking-wider px-1 rounded">
          {daysLate}d late
        </span>
      )}
    </Link>
  );
}

// Larger, dark-mode row used in the Due Today sidebar
function ItemInList({ it, dark }) {
  const k = it.kind || it.type;
  const linkTo = k === "task" ? "/app/tasks" :
    k === "vv" ? `/app/vv/${it.id}` :
    k === "meeting" ? `/app/meetings/${it.id}` :
    k === "subtask" && it.parent_action_id ? `/app/actions/${it.parent_action_id}` :
    `/app/actions/${it.id}`;
  const isDone = ["completed", "closed", "resolved"].includes((it.status || "").toLowerCase());
  const prefix = k === "vv" ? "V&V" : k === "meeting" ? "Meeting" : k === "task" ? "Task" : "Action";
  return (
    <Link to={linkTo} data-testid={`mini-cal-item-${it.id}`}
      className={`block text-[11px] leading-tight rounded-md p-1.5 transition-colors ${
        dark ? "bg-white/5 hover:bg-white/15" : "hover:bg-[#f7f5f2]"
      } ${isDone ? "line-through opacity-55" : ""}`}>
      <div className={`font-mono uppercase tracking-wider text-[9px] font-bold ${dark ? "text-[#ff5f15]" : "text-[#ff4500]"}`}>
        * {prefix}
      </div>
      <div className={`${dark ? "text-white" : "text-[#1a1a1a]"} font-medium truncate`}>
        {it.display_id ? `${it.display_id} — ` : ""}{it.title}
        {it.days_overdue > 0 && (
          <span className="ml-2 inline-block bg-[#ff4500] text-white font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
            {it.days_overdue}d late
          </span>
        )}
      </div>
    </Link>
  );
}
